/**
 * IELTS grading (D-105): the Writing and Speaking copies waiting for a trainer, from the mock and
 * practice exams (`productions`) and from the course (`course-productions`). The trainer rates each
 * criterion of the public rubric (Writing: TA/TR, CC, LR, GRA; Speaking: FC, LR, GRA, P) with a
 * band from 0 to 9 by half bands; the band of the copy is computed with the rounding of the format
 * table and shown as an estimate. The AI's bands stay on the server for the calibration.
 */

import {
  type CourseProduction,
  type Production,
  useAcademyCourseProductionsGradeCreate,
  useAcademyCourseProductionsList,
  useAcademyFormatRetrieve,
  useAcademyProductionsGradeCreate,
  useAcademyProductionsList,
} from "@agencyos/api-client";
import { Badge, type BadgeTone, Button, EmptyState, GlassCard, GlassSheet, SectionHeader, SegmentedControl, SelectField, Skeleton, TextAreaField } from "@agencyos/ui-web";
import { ClipboardCheck } from "lucide-react";
import { type FormEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import "./ielts-admin.css";
import { localized } from "../../components/TranslatedFields";
import { fieldErrors } from "../../lib/settings";
import { dateTimeText } from "../billing/shared";
import { useAdminExam } from "./examContext";
import { AiCorrection, GradingFilters, GradingTags, GradingWarnings, useGradingFilters } from "./GradingTools";
import { type Criterion, rubricsOf } from "./GradingTab";
import { bandSteps, bandText, criteriaBand, roundingOf } from "./ieltsData";
import { useIeltsSkillName } from "./ieltsShared";
import { ErrorList, isObject, num, Pager, str, useAcademyRoles, useRefreshAcademy } from "./shared";

type Source = "exam" | "course";
const SOURCES: Source[] = ["exam", "course"];
const STATUS_TONE: Record<string, BadgeTone> = { draft: "default", submitted: "default", graded: "success", to_trainer: "danger" };

/** One copy, whatever its origin (exam production or course exercise). */
export interface IeltsCopy {
  id: string;
  source: Source;
  skill: "ee" | "eo";
  task: string;
  learner: string;
  createdAt: string;
  prompt: string;
  text: string;
  words: number;
  status: string;
  band: number | null;
  audioUrl: string | null;
  grade: Record<string, unknown>;
  turns: { role: string; text: string }[];
  needs_listening?: boolean;
  review_pending?: boolean;
  waiting_too_long?: boolean;
}

function turnsOf(value: unknown): { role: string; text: string }[] {
  if (!Array.isArray(value)) return [];
  return (value as unknown[]).filter(isObject).flatMap((t) => (str(t.text) ? [{ role: str(t.role), text: str(t.text) }] : []));
}

export function fromProduction(p: Production): IeltsCopy {
  return {
    id: p.id,
    source: "exam",
    skill: p.skill === "eo" ? "eo" : "ee",
    task: String(p.task_number),
    learner: p.learner_email,
    createdAt: p.created_at,
    prompt: p.prompt,
    text: p.text,
    words: p.word_count,
    status: p.status,
    band: p.band,
    audioUrl: p.audio_url,
    grade: isObject(p.grade) ? p.grade : {},
    turns: turnsOf(p.turns),
    needs_listening: p.needs_listening,
    review_pending: p.review_pending,
    waiting_too_long: p.waiting_too_long,
  };
}

export function fromCourse(p: CourseProduction): IeltsCopy {
  return {
    id: p.id,
    source: "course",
    skill: p.type === "speak" ? "eo" : "ee",
    task: p.lesson,
    learner: p.learner_email,
    createdAt: p.created_at,
    prompt: p.prompt,
    text: p.text,
    words: p.word_count,
    status: p.status,
    band: p.band,
    audioUrl: p.audio_url,
    grade: isObject(p.grade) ? p.grade : {},
    turns: [],
  };
}

function useTaskLabel() {
  const { t } = useTranslation();
  const skillName = useIeltsSkillName();
  return (copy: IeltsCopy) =>
    copy.source === "course"
      ? t("academyAdmin.ielts.grading.courseTask", { skill: skillName(copy.skill), lesson: copy.task })
      : t(copy.skill === "ee" ? "academyAdmin.ielts.grading.writingTask" : "academyAdmin.ielts.grading.speakingPart", { skill: skillName(copy.skill), n: copy.task });
}

function previousBands(grade: Record<string, unknown>): Record<string, { band: number | null; comment: string }> {
  const rows = Array.isArray(grade.criteria) ? (grade.criteria as unknown[]).filter(isObject) : [];
  return Object.fromEntries(
    rows.map((c) => [str(c.key), { band: typeof c.band === "number" ? c.band : c.band === undefined ? null : num(c.band), comment: str(c.comment) }]),
  );
}

export function IeltsGradeSheet({ copy, rubric, thresholds, onClose }: { copy: IeltsCopy; rubric: Criterion[]; thresholds: [number, number][]; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const refresh = useRefreshAcademy();
  const taskLabel = useTaskLabel();
  const skillName = useIeltsSkillName();
  const examGrade = useAcademyProductionsGradeCreate({ mutation: { onSuccess: refresh } });
  const courseGrade = useAcademyCourseProductionsGradeCreate({ mutation: { onSuccess: refresh } });
  const grade = copy.source === "exam" ? examGrade : courseGrade;
  const [current, setCurrent] = useState<IeltsCopy>(copy);
  const previous = previousBands(current.grade);
  const source = str(current.grade.source);
  const [bands, setBands] = useState<Record<string, string>>(() =>
    Object.fromEntries(rubric.map((c) => [c.key, previous[c.key]?.band !== null && previous[c.key]?.band !== undefined ? String(previous[c.key]?.band) : ""])),
  );
  const [comments, setComments] = useState<Record<string, string>>(() => Object.fromEntries(rubric.map((c) => [c.key, previous[c.key]?.comment ?? ""])));
  const [comment, setComment] = useState(str(current.grade.comment));
  const keys = rubric.map((c) => c.key);
  const computed = criteriaBand(keys, bands, thresholds);
  const errors = fieldErrors(grade.error);
  const aiBand = typeof current.grade.ai_band === "number" ? current.grade.ai_band : source === "ai" ? current.band : null;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (computed === null) return;
    const data = { criteria: rubric.map((c) => ({ key: c.key, band: Number(bands[c.key]), comment: comments[c.key] ?? "" })), comment };
    const onDone = (next: IeltsCopy) => setCurrent(next);
    if (copy.source === "exam") examGrade.mutate({ id: copy.id, data }, { onSuccess: (r) => r.status === 200 && onDone(fromProduction(r.data)) });
    else courseGrade.mutate({ id: copy.id, data }, { onSuccess: (r) => r.status === 200 && onDone(fromCourse(r.data)) });
  };

  return (
    <GlassSheet open title={taskLabel(current)} onClose={onClose}>
      <div className="stack">
        <div className="row">
          <Badge tone={STATUS_TONE[current.status] ?? "default"}>{t(`academyAdmin.grading.status.${current.status}`)}</Badge>
          <span className="muted">{current.learner}</span>
          <span className="muted">{dateTimeText(current.createdAt, i18n.language)}</span>
        </div>
        <section className="installment-row stack">
          <strong>{t("academyAdmin.grading.prompt")}</strong>
          <p lang="en" className="academy-pre">
            {current.prompt}
          </p>
        </section>
        <section className="stack">
          <strong>{current.skill === "eo" ? t("academyAdmin.grading.recording") : t("academyAdmin.grading.text")}</strong>
          {current.audioUrl ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- its transcript is shown just below as text
            <audio controls preload="none" src={current.audioUrl} aria-label={t("academyAdmin.ielts.grading.replay")} />
          ) : current.skill === "eo" ? (
            <p className="muted">{t("academyAdmin.ielts.grading.noAudio")}</p>
          ) : null}
          {current.turns.length ? (
            <ol className="ielts-turns" lang="en">
              {current.turns.map((turn, i) => (
                <li key={i} className={turn.role === "examiner" ? "ielts-turn ielts-turn--examiner" : "ielts-turn"}>
                  <span className="ao-footnote ao-secondary">{turn.role === "examiner" ? t("academyAdmin.ielts.grading.examiner") : t("academyAdmin.ielts.grading.candidate")}</span>
                  <span>{turn.text}</span>
                </li>
              ))}
            </ol>
          ) : current.text ? (
            <p lang="en" className="installment-row academy-pre">
              {current.text}
            </p>
          ) : (
            <p className="muted">{t("academyAdmin.grading.noText")}</p>
          )}
          {current.skill === "ee" ? <span className="muted">{t("academyAdmin.grading.words", { count: current.words })}</span> : null}
          {str(current.grade.word_warning) ? <p className="notice-warning installment-row">{str(current.grade.word_warning)}</p> : null}
        </section>
        {current.band !== null ? (
          <section className="installment-row stack">
            <div className="row">
              <strong>{t("academyAdmin.ielts.grading.currentBand", { band: bandText(current.band, i18n.language) })}</strong>
              <Badge tone={source === "ai" ? "offer" : "success"}>{source === "ai" ? t("academyAdmin.grading.byAi") : t("academyAdmin.grading.byTrainer")}</Badge>
              {aiBand !== null && source !== "ai" ? <span className="muted">{t("academyAdmin.ielts.grading.aiBand", { band: bandText(aiBand, i18n.language) })}</span> : null}
            </div>
            <p className="muted">{t("academyAdmin.ielts.estimate")}</p>
          </section>
        ) : null}

        <AiCorrection grade={current.grade} />
        <form className="stack" onSubmit={onSubmit} noValidate aria-label={t("academyAdmin.grading.form")}>
          <h3 className="ao-headline">{t("academyAdmin.ielts.grading.rubric", { skill: skillName(current.skill) })}</h3>
          {rubric.length ? null : <p className="ko">{t("academyAdmin.grading.noRubric")}</p>}
          <fieldset className="plain-fieldset stack" disabled={!canEdit}>
            {rubric.map((c) => {
              const ai = previous[c.key];
              return (
                <div key={c.key} className="installment-row ielts-criterion">
                  <SelectField
                    label={t("academyAdmin.ielts.grading.criterion", { label: localized(c.label, i18n.language) || c.key })}
                    value={bands[c.key] ?? ""}
                    hint={ai && ai.band !== null && source === "ai" ? t("academyAdmin.ielts.grading.aiGave", { band: bandText(ai.band, i18n.language) }) : undefined}
                    options={[{ value: "", label: t("academyAdmin.ielts.grading.pick") }, ...bandSteps(c.max).map((b) => ({ value: String(b), label: bandText(b, i18n.language) }))]}
                    onChange={(e) => setBands((all) => ({ ...all, [c.key]: e.target.value }))}
                  />
                  <TextAreaField
                    label={t("academyAdmin.grading.criterionComment")}
                    rows={2}
                    value={comments[c.key] ?? ""}
                    onChange={(e) => setComments((all) => ({ ...all, [c.key]: e.target.value }))}
                  />
                </div>
              );
            })}
            <TextAreaField label={t("academyAdmin.grading.comment")} rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
            <div className="ielts-band-total" aria-live="polite">
              <span className="ao-footnote ao-secondary">{t("academyAdmin.ielts.grading.computed")}</span>
              <strong className="big-number ao-num">{bandText(computed, i18n.language)}</strong>
              <span className="muted">{t("academyAdmin.ielts.estimate")}</span>
            </div>
            {errors.criteria ? (
              <p role="alert" className="ao-field__error">
                {errors.criteria}
              </p>
            ) : (
              <ErrorList error={grade.error} />
            )}
            <div className="row">
              <Button type="submit" disabled={grade.isPending || computed === null}>
                {grade.isPending ? t("common.loading") : t("academyAdmin.grading.grade")}
              </Button>
              {grade.isSuccess ? (
                <span role="status" className="ok">
                  {t("academyAdmin.grading.graded")}
                </span>
              ) : null}
            </div>
          </fieldset>
        </form>
      </div>
    </GlassSheet>
  );
}

export function IeltsGradingTab() {
  const { t, i18n } = useTranslation();
  const { params } = useAdminExam();
  const taskLabel = useTaskLabel();
  const [source, setSource] = useState<Source>("exam");
  const grading = useGradingFilters();
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<IeltsCopy | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const format = useAcademyFormatRetrieve(params);
  const formatData = format.data?.status === 200 ? format.data.data : undefined;
  const rubrics = useMemo(() => rubricsOf(formatData?.rubrics), [formatData]);
  const thresholds = useMemo(() => roundingOf(formatData?.details), [formatData]);
  const filters = { ...params, ...grading.params, page };
  const exam = useAcademyProductionsList(filters, { query: { enabled: source === "exam" } });
  const course = useAcademyCourseProductionsList(filters, { query: { enabled: source === "course" } });
  const examData = exam.data?.status === 200 ? exam.data.data : undefined;
  const courseData = course.data?.status === 200 ? course.data.data : undefined;
  const data = source === "exam" ? examData : courseData;
  const rows: IeltsCopy[] = source === "exam" ? (examData?.results ?? []).filter((p) => p.skill === "ee" || p.skill === "eo").map(fromProduction) : (courseData?.results ?? []).map(fromCourse);

  return (
    <GlassCard as="section" className="stack" aria-labelledby="ielts-grading-title">
      <SectionHeader id="ielts-grading-title" title={t("academyAdmin.ielts.grading.title")} subtitle={t("academyAdmin.ielts.grading.intro")} />
      <GradingWarnings />
      <div className="ielts-toolbar">
        <SegmentedControl
          label={t("academyAdmin.ielts.grading.source")}
          value={source}
          options={SOURCES.map((s) => ({ value: s, label: t(`academyAdmin.ielts.grading.sources.${s}`) }))}
          onChange={(value) => {
            setSource(value);
            setPage(1);
          }}
        />
        <GradingFilters
          values={grading.values}
          onChange={(next) => {
            grading.setValues(next);
            setPage(1);
          }}
        />
      </div>
      {data === undefined ? (
        <Skeleton style={{ height: 200 }} />
      ) : rows.length ? (
        <>
          <p className="muted">{t("academyAdmin.count", { count: data.count })}</p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("academyAdmin.grading.learner")}</th>
                  <th>{t("academyAdmin.grading.task")}</th>
                  <th>{t("academyAdmin.grading.date")}</th>
                  <th>{t("academyAdmin.grading.wordCount")}</th>
                  <th>{t("academyAdmin.ielts.grading.band")}</th>
                  <th>{t("academyAdmin.statusLabel")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((copy) => {
                  const by = str(copy.grade.source);
                  return (
                    <tr key={copy.id}>
                      <td>
                        <button type="button" className="link" onClick={() => setOpen(copy)}>
                          {copy.learner}
                        </button>
                      </td>
                      <td className="ielts-nowrap">{taskLabel(copy)}</td>
                      <td className="ielts-nowrap">{dateTimeText(copy.createdAt, i18n.language)}</td>
                      <td className="ao-num">{copy.skill === "ee" ? copy.words : "—"}</td>
                      <td className="ielts-nowrap">
                        <span className="ao-num">{bandText(copy.band, i18n.language)}</span>
                        {by ? <span className="muted"> · {by === "ai" ? t("academyAdmin.grading.byAi") : t("academyAdmin.grading.byTrainer")}</span> : null}
                        <GradingTags row={copy} />
                      </td>
                      <td>
                        <Badge tone={STATUS_TONE[copy.status] ?? "default"}>{t(`academyAdmin.grading.status.${copy.status}`)}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : (
        <EmptyState compact icon={<ClipboardCheck size={22} strokeWidth={1.75} />} title={t(`academyAdmin.grading.empty.${grading.values.status}`)} />
      )}
      {open ? <IeltsGradeSheet key={`${open.source}-${open.id}`} copy={open} rubric={rubrics[open.skill] ?? []} thresholds={thresholds} onClose={close} /> : null}
    </GlassCard>
  );
}
